import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import VoicePreview from './VoicePreview';
import voiceBotService from '../services/VoiceBotService';
import agentService from '../services/AgentService';
import adminAgentService from '../services/AdminAgentService';

const VOICES = ['marin', 'cedar', 'alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse', 'beacon', 'bossa', 'cinder', 'delta', 'gleam', 'meridian', 'quartz', 'ripple', 'stone', 'tempo', 'vesper', 'willow'];
const emptyBot = { voiceBotName: '', agentId: '', phoneNumber: '', voiceEnable: false, voiceModel: 'gpt-live-1', voice: 'marin', maxCallDuration: 600, greeting: '' };

export default function VoiceBots({ embedded = false }) {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const [bots, setBots] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyBot);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [data, agentData] = await Promise.all([voiceBotService.list(clientId), clientId ? adminAgentService.getAgents(clientId) : agentService.getAgents()]);
            setBots(data);
            setAgents(agentData);
        } catch (_) { setError(t('voiceBots.loadError')); }
        finally { setLoading(false); }
    }, [clientId, t]);
    useEffect(() => { load(); }, [load]);
    const change = (key, value) => setForm((old) => ({ ...old, [key]: value }));
    const open = (bot) => {
        setError('');
        setEditing(bot || {});
        setForm(bot ? Object.fromEntries(Object.keys(emptyBot).map((key) => [key, bot[key]])) : { ...emptyBot, greeting: t('voiceBots.defaultGreeting') });
    };
    const duration = Number(form.maxCallDuration);
    const valid = form.voiceBotName.trim() && form.agentId && /^\+[1-9][0-9]{7,14}$/.test(form.phoneNumber)
        && form.voiceModel === 'gpt-live-1' && form.greeting.trim()
        && Number.isInteger(duration) && duration >= 60 && duration <= 3600;
    const save = async () => {
        if (!valid || saving) return;
        setSaving(true);
        setError('');
        try {
            await voiceBotService.save(clientId, editing.voiceBotId, { ...form, maxCallDuration: duration });
            setEditing(null);
            await load();
        } catch (e) { setError(t(e.status === 409 ? 'voiceBots.conflict' : 'voiceBots.saveError')); }
        finally { setSaving(false); }
    };
    const remove = async () => {
        setSaving(true);
        try { await voiceBotService.remove(clientId, deleting.voiceBotId); setDeleting(null); await load(); }
        catch (_) { setError(t('voiceBots.deleteError')); }
        finally { setSaving(false); }
    };
    return (
        <Box sx={{ p: embedded ? 0 : 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">{t('voiceBots.title')}</Typography>
                <Button variant="contained" onClick={() => open(null)} disabled={loading || !agents.length}>{t('voiceBots.add')}</Button>
            </Stack>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography color="text.secondary" sx={{ mb: 2 }}>{t('voiceBots.description')}</Typography>
            {!loading && !agents.length && <Alert severity="info" sx={{ mb: 2 }}>{t('voiceBots.needAgent')}</Alert>}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead><TableRow>{['name', 'phone', 'agent', 'status', 'actions'].map((key) => <TableCell key={key}>{t(`voiceBots.${key}`)}</TableCell>)}</TableRow></TableHead>
                    <TableBody>
                        {bots.map((bot) => <TableRow key={bot.voiceBotId}>
                            <TableCell>{bot.voiceBotName}</TableCell><TableCell>{bot.phoneNumber}</TableCell>
                            <TableCell>{agents.find((agent) => agent.agentId === bot.agentId)?.agentName || bot.agentId}</TableCell>
                            <TableCell><Chip size="small" color={bot.voiceEnable ? 'success' : 'default'} label={t(bot.voiceEnable ? 'voiceBots.enabled' : 'voiceBots.disabled')} /></TableCell>
                            <TableCell><Button onClick={() => open(bot)}>{t('voiceBots.edit')}</Button><Button color="error" onClick={() => setDeleting(bot)}>{t('voiceBots.delete')}</Button></TableCell>
                        </TableRow>)}
                        {!bots.length && <TableRow><TableCell colSpan={5}>{t(loading ? 'voiceBots.loading' : 'voiceBots.empty')}</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={editing !== null} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ py: 1.5 }}>{t(editing?.voiceBotId ? 'voiceBots.edit' : 'voiceBots.add')}</DialogTitle>
                <DialogContent sx={{ pb: 1 }}><Stack spacing={1.25} sx={{ pt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                    <TextField size="small" fullWidth label={t('voiceBots.name')} value={form.voiceBotName} onChange={(e) => change('voiceBotName', e.target.value)} inputProps={{ maxLength: 120 }} disabled={saving} required />
                    <TextField size="small" fullWidth select label={t('voiceBots.agent')} value={form.agentId} onChange={(e) => change('agentId', e.target.value)} disabled={saving} required>
                        {form.agentId && !agents.some((agent) => agent.agentId === form.agentId) && <MenuItem value={form.agentId}>{t('voiceBots.missingAgent')}</MenuItem>}
                        {agents.map((agent) => <MenuItem key={agent.agentId} value={agent.agentId}>{agent.agentName || agent.agentId}</MenuItem>)}
                    </TextField>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                    <TextField size="small" fullWidth label={t('voiceBots.phone')} value={form.phoneNumber} onChange={(e) => change('phoneNumber', e.target.value.trim())} helperText={t('voiceBots.phoneHelp')} inputProps={{ maxLength: 16 }} disabled={saving} required />
                    <TextField size="small" fullWidth label={t('voiceBots.duration')} type="number" value={form.maxCallDuration} onChange={(e) => change('maxCallDuration', e.target.value)} inputProps={{ min: 60, max: 3600, step: 1 }} helperText={t('voiceBots.durationHelp')} disabled={saving} required />
                    </Box>
                    <FormControlLabel control={<Switch size="small" checked={form.voiceEnable} onChange={(e) => change('voiceEnable', e.target.checked)} disabled={saving} />} label={t('voiceBots.enable')} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                    <TextField size="small" fullWidth label={t('voiceBots.model')} value={form.voiceModel} disabled required />
                    <TextField size="small" fullWidth select label={t('voiceBots.voice')} value={form.voice} onChange={(e) => change('voice', e.target.value)} disabled={saving}>
                        {VOICES.map((voice) => <MenuItem key={voice} value={voice}>{voice}</MenuItem>)}
                    </TextField>
                    </Box>
                    <TextField size="small" fullWidth label={t('voiceBots.greeting')} value={form.greeting} onChange={(e) => change('greeting', e.target.value)} multiline minRows={2} inputProps={{ maxLength: 500 }} disabled={saving} required />
                    {editing !== null && <VoicePreview key={editing.voiceBotId || 'new'} clientId={clientId} voice={form.voice} greeting={form.greeting} disabled={saving} />}
                    <Alert severity="info">{t('voiceBots.setupHelp')}</Alert>
                </Stack></DialogContent>
                <DialogActions sx={{ px: 3, py: 1 }}><Button onClick={() => setEditing(null)} disabled={saving}>{t('voiceBots.cancel')}</Button><Button variant="contained" onClick={save} disabled={!valid || saving}>{t('voiceBots.save')}</Button></DialogActions>
            </Dialog>
            <Dialog open={Boolean(deleting)} onClose={() => !saving && setDeleting(null)}>
                <DialogTitle>{t('voiceBots.delete')}</DialogTitle><DialogContent>{t('voiceBots.deleteConfirm', { name: deleting?.voiceBotName })}</DialogContent>
                <DialogActions><Button onClick={() => setDeleting(null)} disabled={saving}>{t('voiceBots.cancel')}</Button><Button color="error" onClick={remove} disabled={saving}>{t('voiceBots.delete')}</Button></DialogActions>
            </Dialog>
        </Box>
    );
}
